/**
 * 100 Canales Dijeron — Solo player.
 * - Lista de encuestas activas → juega una → 90s para adivinar top-5 respuestas.
 * - Backend hace el match server-side para que las keywords nunca lleguen al cliente.
 * - Al terminar (3 strikes, todas adivinadas, o timer 0) → guarda el score.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trophy, X, Clock, Zap, ChevronRight, Home, Play,
  Volume2, RefreshCw, Loader2, ArrowLeft, Star } from "lucide-react";

import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import { Carlos, Ana } from "@/components/characters/Characters";
import { Input } from "@/components/ui/input";
import ShareAppButton from "@/components/ShareAppButton";
import { playHit, playMiss, playApplause, playStart, playCountdown, isEnabled as soundEnabled, toggleEnabled as toggleSound } from "@/utils/sounds";
import { VolumeX, Swords } from "lucide-react";

const scoreColor = (v) => (v >= 80 ? "#2E7D32" : v >= 40 ? "#B26A00" : "#E3000F");

function Header() {
  const [snd, setSnd] = useState(soundEnabled());
  return (
    <div className="flex items-center justify-between mb-4">
      <Link to="/magazine" className="brutal-btn bg-white px-3 py-2 text-sm inline-flex items-center gap-1" data-testid="dijeron-back">
        <ArrowLeft className="w-4 h-4" /> Volver
      </Link>
      <CanalesLogo width={110} />
      <div className="flex items-center gap-2">
        <button
          onClick={() => setSnd(toggleSound())}
          data-testid="dijeron-sound-toggle"
          title={snd ? "Silenciar" : "Activar sonidos"}
          className={`brutal-btn px-3 py-2 text-sm inline-flex items-center gap-1 ${snd ? "bg-canales-yellow text-black" : "bg-white text-neutral-500"}`}
        >
          {snd ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <ShareAppButton size="sm" variant="yellow" />
      </div>
    </div>
  );
}

function GameBackground({ children }) {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0d1a3f" }}>
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.25) 1.5px, transparent 1.5px)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="absolute -left-40 top-24 w-[720px] h-[100px] opacity-80 pointer-events-none"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }} />
      <div className="absolute -right-40 bottom-40 w-[520px] h-[70px] opacity-70 pointer-events-none"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }} />
      <div className="relative max-w-4xl mx-auto p-4 md:p-6">{children}</div>
    </div>
  );
}

function BoardSlot({ answer, hit, index, lang }) {
  const revealed = hit
    ? (lang === "en" && hit.text_en ? hit.text_en : hit.text)
    : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`brutal flex items-center justify-between px-4 py-3 ${
        hit ? "bg-canales-cream text-black" : "bg-canales-blue text-white"
      }`}
      style={{ minHeight: "64px" }}
      data-testid={`dijeron-slot-${answer.rank}`}
    >
      {hit ? (
        <>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-display font-black text-2xl text-canales-red shrink-0">
              #{answer.rank}
            </span>
            <span className="font-display font-black text-base md:text-lg tracking-wide truncate">
              {revealed}
            </span>
          </div>
          <div className="brutal-sm bg-canales-red text-white px-3 py-1 font-display font-black text-xl shrink-0">
            {hit.points}
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-between">
          <span className="font-display font-black text-3xl text-canales-yellow">
            {answer.rank}
          </span>
          <span className="text-white/50 font-mono text-sm hidden sm:inline">
            {"·".repeat(Math.min(answer.text_len || 8, 20))}
          </span>
        </div>
      )}
    </motion.div>
  );
}

function StrikesRow({ count }) {
  return (
    <div className="flex gap-2">
      {[1, 2, 3].map((n) => (
        <motion.div
          key={n}
          animate={n <= count ? { rotate: [0, -15, 15, 0] } : {}}
          className={`w-12 h-12 brutal-sm flex items-center justify-center font-display font-black text-3xl ${
            n <= count ? "bg-canales-red text-white" : "bg-white text-neutral-200"
          }`}
        >
          <X className="w-7 h-7" strokeWidth={4} />
        </motion.div>
      ))}
    </div>
  );
}

/** ----- Landing (list of surveys) ----- */
function DijeronLanding() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get("/dijeron/surveys"),
          api.get("/dijeron/leaderboard?limit=10"),
        ]);
        setSurveys(s.data || []);
        setBoard(l.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <GameBackground>
      <Header />
      <motion.div
        initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="text-center mb-6"
      >
        <h1 className="font-display font-black text-5xl md:text-7xl text-canales-yellow leading-none"
          style={{ WebkitTextStroke: "3px black", textShadow: "6px 6px 0px #E3000F", transform: "rotate(-1.2deg)" }}>
          100 CANALES
        </h1>
        <h1 className="font-display font-black text-5xl md:text-7xl text-white leading-none mt-1"
          style={{ WebkitTextStroke: "3px black", textShadow: "6px 6px 0px #E3000F", transform: "rotate(1.2deg)" }}>
          DIJERON…
        </h1>
        <p className="mt-3 text-white/80 font-body text-sm md:text-base">
          Encuestamos a 100 vendedores de Canales. Adivina las respuestas más comunes.
        </p>
      </motion.div>

      {loading ? (
        <div className="text-center text-white/70 py-8">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => nav("/juego/canales-dijeron/duelo")}
              data-testid="dijeron-battle-cta"
              className="brutal-btn bg-canales-red text-white p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform"
            >
              <Swords className="w-8 h-8" />
              <div className="text-left">
                <div className="font-display font-black text-lg leading-tight">
                  Duelo por Tiendas
                </div>
                <div className="text-xs font-body opacity-90">
                  Dos vendedores compiten en vivo · 3 rondas
                </div>
              </div>
            </button>
            <div className="brutal bg-white p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full border-2 border-black flex items-center justify-center bg-canales-yellow">
                <Trophy className="w-6 h-6" />
              </div>
              <div className="text-sm font-body">
                <b>Modo solo</b> — elige una encuesta abajo. Tu mejor puntuación
                por encuesta cuenta en el ranking global.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {surveys.map((s, i) => (
              <motion.button
                key={s.survey_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => nav(`/juego/canales-dijeron/${s.slug}`)}
                data-testid={`dijeron-pick-${s.slug}`}
                className="brutal bg-white text-left p-4 hover:-translate-y-0.5 transition-transform"
              >
                <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">
                  {s.category}
                </div>
                <div className="mt-1 font-display font-black text-lg leading-tight">
                  {s.question_es}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs font-body text-neutral-500">
                  <span>{(s.answers || []).length} respuestas</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {s.time_seconds || 90}s
                  </span>
                  <span className="brutal-sm bg-canales-yellow text-black px-2 py-0.5 font-display font-black">
                    <Play className="w-3 h-3 inline-block mr-1" /> Jugar
                  </span>
                </div>
              </motion.button>
            ))}
            {surveys.length === 0 && (
              <div className="col-span-2 brutal bg-white p-6 text-center text-neutral-500">
                Aún no hay encuestas. Un admin puede crear en <b>/admin/dijeron</b>.
              </div>
            )}
          </div>

          {board.length > 0 && (
            <section className="brutal bg-white p-4" data-testid="dijeron-leaderboard">
              <h2 className="font-display font-black text-2xl flex items-center gap-2">
                <Trophy className="w-6 h-6 text-canales-red" /> Ranking Global
              </h2>
              <ol className="mt-3 space-y-1">
                {board.map((p, i) => (
                  <li key={p.user_id} className="flex items-center justify-between text-sm py-1">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`brutal-sm w-7 h-7 flex items-center justify-center font-display font-black text-xs ${i === 0 ? "bg-canales-yellow" : i === 1 ? "bg-neutral-200" : i === 2 ? "bg-orange-200" : "bg-white"}`}>
                        {i + 1}
                      </span>
                      <span className="font-display font-bold truncate">{p.player_name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">{p.player_role}</span>
                    </span>
                    <span className="font-display font-black text-lg text-canales-blue">
                      {p.total}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}
    </GameBackground>
  );
}

/** ----- Player (single survey) ----- */
function DijeronPlay() {
  const { slug } = useParams();
  const { user } = useAuth() || {};
  const { lang } = useLang();
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [status, setStatus] = useState("loading"); // loading|ready|playing|done
  const [hits, setHits] = useState({});             // rank -> {text, points, rank}
  const [strikes, setStrikes] = useState(0);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(90);
  const [input, setInput] = useState("");
  const [flash, setFlash] = useState(null);         // "hit" | "miss" | null
  const startedAt = useRef(0);
  const posted = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/dijeron/surveys/${slug}`);
        setSurvey(data);
        setTime(data.time_seconds || 90);
        setStatus("ready");
      } catch {
        toast.error("Encuesta no encontrada");
        nav("/juego/canales-dijeron", { replace: true });
      }
    })();
  }, [slug, nav]);

  useEffect(() => {
    if (status !== "playing") return;
    const t = setInterval(() => {
      setTime((v) => {
        const next = Math.max(0, v - 1);
        if (next > 0 && next <= 10) playCountdown();
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [status]);

  const total = survey?.answers?.length || 0;
  const done = strikes >= 3 || Object.keys(hits).length >= total || time <= 0;

  useEffect(() => {
    if (status === "playing" && done && !posted.current) {
      posted.current = true;
      setStatus("done");
      playApplause();
      const duration = Math.round((Date.now() - startedAt.current) / 1000);
      if (user?.user_id) {
        api.post("/dijeron/plays", {
          survey_id: survey.survey_id,
          correct_ranks: Object.keys(hits).map((n) => parseInt(n, 10)),
          score,
          strikes,
          duration_seconds: duration,
          player_name: user?.name,
        }).catch((e) => toast.error(e?.response?.data?.detail || "Error guardando"));
      }
    }
  }, [done, status, hits, score, strikes, survey, user]);

  const startGame = () => {
    setStatus("playing");
    startedAt.current = Date.now();
    playStart();
  };

  const submitGuess = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || status !== "playing") return;
    try {
      const { data } = await api.post("/dijeron/guess", {
        survey_id: survey.survey_id,
        guess: input,
        already_hit: Object.keys(hits).map((n) => parseInt(n, 10)),
      });
      setInput("");
      if (data.matched) {
        setHits((h) => ({ ...h, [data.rank]: data }));
        setScore((s) => s + (data.points || 0));
        setFlash("hit");
        playHit();
      } else {
        setStrikes((s) => s + 1);
        setFlash("miss");
        playMiss();
      }
      setTimeout(() => setFlash(null), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (status === "loading" || !survey) {
    return (
      <GameBackground>
        <div className="text-center text-white/70 py-24">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      </GameBackground>
    );
  }

  return (
    <GameBackground>
      <Header />

      {/* Question */}
      <div className="relative mb-5 flex items-start gap-3">
        <div className="hidden md:block shrink-0">
          <Carlos size={100} />
        </div>
        <div className="flex-1 brutal bg-canales-cream p-4 relative">
          <div className="absolute -left-3 top-8 w-6 h-6 bg-canales-cream border-l-4 border-b-4 border-black hidden md:block"
            style={{ transform: "rotate(45deg)" }} />
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red mb-1">
            {lang === "en" ? "We surveyed 100 Canales sellers" : "Encuestamos a 100 vendedores de Canales"}
          </div>
          <div className="font-display font-black text-xl md:text-2xl leading-tight">
            "{lang === "en" && survey.question_en ? survey.question_en : survey.question_es}"
          </div>
        </div>
      </div>

      {/* Board */}
      <div className="grid grid-cols-1 gap-2 mb-5" data-testid="dijeron-board">
        {survey.answers.map((a, i) => (
          <BoardSlot key={a.rank} answer={a} hit={hits[a.rank]} index={i} lang={lang} />
        ))}
      </div>

      {/* Controls */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="brutal bg-white p-3">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-600 mb-1">
            Strikes
          </div>
          <StrikesRow count={strikes} />
        </div>
        <motion.div
          animate={time <= 10 && status === "playing" ? { scale: [1, 1.05, 1] } : {}}
          transition={{ repeat: Infinity, duration: 0.5 }}
          className={`brutal p-3 flex items-center gap-2 ${time <= 10 && status === "playing" ? "bg-canales-red text-white" : "bg-canales-blue text-white"}`}
        >
          <Clock className="w-6 h-6" />
          <div>
            <div className="text-[10px] font-display font-black uppercase tracking-widest">Tiempo</div>
            <div className="font-display font-black text-3xl leading-none">
              00:{time.toString().padStart(2, "0")}
            </div>
          </div>
        </motion.div>
        <div className="brutal bg-canales-yellow p-3 flex items-center gap-2">
          <Trophy className="w-6 h-6" />
          <div>
            <div className="text-[10px] font-display font-black uppercase tracking-widest">Puntos</div>
            <div className="font-display font-black text-3xl leading-none" data-testid="dijeron-score">
              {score}
            </div>
          </div>
        </div>
      </div>

      {/* Input or start button */}
      {status === "ready" && (
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          onClick={startGame}
          data-testid="dijeron-start"
          className="w-full brutal-btn bg-canales-red text-white px-6 py-4 text-2xl font-display font-black inline-flex items-center justify-center gap-2"
        >
          <Play className="w-6 h-6" /> ¡EMPEZAR!
        </motion.button>
      )}
      {status === "playing" && (
        <form onSubmit={submitGuess} className="brutal bg-white p-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-canales-red shrink-0" />
          <Input
            data-testid="dijeron-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu respuesta y ENTER…"
            autoFocus
            className="brutal-sm flex-1 py-4 text-base"
          />
          <button
            type="submit"
            data-testid="dijeron-submit"
            className={`brutal-btn px-5 py-3 font-display font-black inline-flex items-center gap-1 ${flash === "hit" ? "bg-green-600 text-white" : flash === "miss" ? "bg-canales-red text-white" : "bg-canales-blue text-white"}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </form>
      )}
      {status === "done" && (
        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
          className="brutal bg-white p-6 text-center space-y-3" data-testid="dijeron-done">
          <Trophy className="w-14 h-14 text-canales-red mx-auto" />
          <h2 className="font-display font-black text-4xl">¡Terminó!</h2>
          <div className="font-display font-black text-6xl" style={{ color: scoreColor((score / (survey.answers.reduce((s, a) => s + a.points, 0) || 1)) * 100) }}>
            {score} pts
          </div>
          <div className="text-sm font-body text-neutral-500">
            {Object.keys(hits).length} de {total} respuestas · {strikes} strikes
          </div>
          {/* Reveal all remaining answers */}
          <details className="text-left">
            <summary className="cursor-pointer font-display font-bold underline">Ver todas las respuestas</summary>
            <ul className="mt-2 space-y-1 text-sm">
              {survey.answers.map((a) => (
                <li key={a.rank} className="flex items-center justify-between">
                  <span>#{a.rank} <b>{hits[a.rank]?.text || "•••"}</b></span>
                  <span className="font-mono">{a.points}</span>
                </li>
              ))}
            </ul>
          </details>
          <div className="flex gap-2 justify-center pt-2">
            <button onClick={() => window.location.reload()} className="brutal-btn bg-canales-blue text-white px-4 py-2 inline-flex items-center gap-1">
              <RefreshCw className="w-4 h-4" /> Otra vez
            </button>
            <Link to="/juego/canales-dijeron" className="brutal-btn bg-white px-4 py-2 inline-flex items-center gap-1">
              <Home className="w-4 h-4" /> Otras encuestas
            </Link>
          </div>
        </motion.div>
      )}
    </GameBackground>
  );
}

export default function CanalesDijeron() {
  const { slug } = useParams();
  return slug ? <DijeronPlay /> : <DijeronLanding />;
}
